import {
  Actions as EducationActions,
  type EducationAction,
} from 'src/components/Profile/Education/actions';
import {
  getConfirmedQualificationsFromCache,
  getUnconfirmedQualificationsFromCache,
} from 'src/components/Profile/Education/data';
import type { Analytics } from 'src/middlewares/createAnalyticsMiddleware';
import type {
  ConfirmedActionName,
  UnconfirmedActionName,
} from 'src/modules/analytics/types';
import type { AppConfig } from 'src/modules/appConfig/types';
import type { ApiClient } from 'src/types';

import { instrumentAnalytics } from '../analyticsMiddlewareUtils';

const currentPageSection = 'education';

export const createEducationAnalyticsMiddleware = (
  analytics: Analytics,
  { apolloClient }: ApiClient,
  { language: languageCode, zone }: AppConfig,
) =>
  instrumentAnalytics(analytics) &&
  (() =>
    (next: (action: EducationAction) => void) =>
    (action: EducationAction) => {
      const { updateUnconfirmedCard, updateCard } = analytics;

      switch (action.type) {
        case EducationActions.UPDATE_QUALIFICATION_START: {
          const { actionName, payload } = action;

          if (actionName) {
            const { id, highlights } = payload.updateQualificationInput;

            const { confirmedQualifications } =
              getConfirmedQualificationsFromCache({
                ...apolloClient,
                languageCode,
                zone,
              });
            const { unconfirmedQualifications } =
              getUnconfirmedQualificationsFromCache({
                apolloClient,
                languageCode,
                zone,
              });

            const existingQualification = [
              ...confirmedQualifications,
              ...unconfirmedQualifications,
            ].find((qualification) => qualification.id === id);

            const existingQualificationHighlights = Boolean(
              existingQualification?.highlights,
            );

            const updatedHighlights = Boolean(highlights?.length);

            const isDescriptionAdded = Boolean(
              existingQualificationHighlights === false &&
              updatedHighlights === true,
            );

            updateCard({
              actionName: actionName as ConfirmedActionName,
              currentPageSection,
              customProps: {
                isDescriptionAdded,
              },
            });
          }
          break;
        }

        case EducationActions.REMOVE_QUALIFICATION_START: {
          const { actionName } = action;

          if (actionName) {
            updateCard({
              actionName: 'DELETE',
              currentPageSection,
            });
          }

          break;
        }
        case EducationActions.ADD_UNCONFIRMED_QUALIFICATION_START: {
          const { actionName, meta } = action;
          const { analytics: { contentRead = false } = {} } = meta || {};

          if (actionName) {
            updateUnconfirmedCard({
              actionName: actionName as UnconfirmedActionName,
              contentRead,
              actionOrigin: 'resume parsing nudge',
              currentPageSection,
            });
          }
          break;
        }
        case EducationActions.REJECT_UNCONFIRMED_QUALIFICATION_START: {
          const { actionName } = action;

          if (actionName) {
            updateUnconfirmedCard({
              actionName: 'REJECT',
              actionOrigin: 'resume parsing nudge',
              currentPageSection,
            });
          }
          break;
        }

        case EducationActions.UPDATE_VERIFIED_QUALIFICATION_SUCCESS: {
          updateCard({
            actionName: 'EDIT',
            currentPageSection,
          });
          break;
        }

        default: {
          break;
        }
      }
      return next(action);
    });
